import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Cake, PaperPlaneTilt, PencilSimple, Warning } from "@phosphor-icons/react";

/**
 * v0.98 · Card "Aniversário do cliente" — versão CONTIDA (Opção A).
 *
 * Redesign monocromático seguindo o design system Apple/AgentUS:
 *   • Fundo branco com borda slate-200 (idêntico aos KPI cards)
 *   • Ícone em quadrado slate-900
 *   • Tipografia hierarquia slate-900 / 600 / 500
 *   • Toggle shadcn-like preto/slate
 *   • Preview em bg-slate-50 (não mais um outdoor de festa)
 *   • Variáveis com nomes PT-BR claros pro usuário final
 */

// Variáveis PT-BR expostas na UI. Bate 1-a-1 com engines/template_vars.py.
// O backend (via `normalize_template`) traduz pra tokens canônicos.
const VARS_PT = [
  { token: "{nome_cliente}", desc: "Primeiro nome" },
  { token: "{nome_empresa}", desc: "Nome do seu negócio" },
];

function renderPreview(template) {
  if (!template) return "";
  // Substitui placeholders (aceita PT-BR novos + inglês antigos) por
  // valores de exemplo pra preview no card.
  return template
    // PT-BR (novos)
    .replace(/\{nome_cliente\}/g, "Maria")
    .replace(/\{nome_completo\}/g, "Maria Silva")
    .replace(/\{nome_empresa\}/g, "sua empresa")
    .replace(/\{beneficio_aniversario\}/g, "10% em qualquer serviço")
    .replace(/\{brinde\}/g, "um mimo especial")
    // Inglês (legacy)
    .replace(/\{first_name\}/g, "Maria")
    .replace(/\{customer_name\}/g, "Maria Silva")
    .replace(/\{tenant_name\}/g, "sua empresa")
    .replace(/\{birthday_perk\}/g, "10% em qualquer serviço")
    .replace(/\{gift\}/g, "um mimo especial");
}

export default function BirthdayCampaignCard() {
  const [settings, setSettings] = useState(null);
  const [todayCount, setTodayCount] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const load = async () => {
    try {
      const [s, t] = await Promise.all([
        api.get("/birthday-campaign/settings"),
        api.get("/birthday-campaign/today"),
      ]);
      setSettings(s.data);
      setDraft(s.data.message_template || "");
      setTodayCount(t.data?.count ?? 0);
    } catch (e) {
      // silencioso
    }
  };
  useEffect(() => { load(); }, []);

  const toggleEnabled = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const r = await api.put("/birthday-campaign/settings", {
        enabled: !settings.enabled,
      });
      setSettings(r.data);
      toast.success(r.data.enabled ? "Automático ligado ✓" : "Automático desligado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao alterar");
    } finally {
      setSaving(false);
    }
  };

  const saveTemplate = async () => {
    setSaving(true);
    try {
      const r = await api.put("/birthday-campaign/settings", {
        message_template: draft,
      });
      setSettings(r.data);
      setEditing(false);
      toast.success("Mensagem atualizada.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar mensagem");
    } finally {
      setSaving(false);
    }
  };

  const dispatchNow = async () => {
    if (!window.confirm(
      `Enviar agora a mensagem de aniversário para ${todayCount} cliente(s)?\n`
      + `Só clientes com marketing opt-in ativo receberão.`
    )) return;
    setDispatching(true);
    try {
      await api.post("/birthday-campaign/dispatch-now");
      toast.success("Disparado. Confira em Auditoria.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao disparar");
    } finally {
      setDispatching(false);
    }
  };

  if (!settings) return null;

  const enabled = settings.enabled;
  const preview = renderPreview(editing ? draft : settings.message_template);
  const noBirthdaysToday = (todayCount ?? 0) === 0;

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white shadow-sm"
      data-testid="birthday-campaign-card"
    >
      {/* Header ─────────────────────────────────────────────── */}
      <div className="px-5 py-4 flex items-center justify-between border-b border-slate-100">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-slate-900 text-white flex items-center justify-center flex-shrink-0">
            <Cake size={16} weight="fill" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900 leading-tight">
              Aniversário do cliente
            </div>
            <div className="text-xs text-slate-500 mt-0.5 leading-snug">
              Mensagem automática às 9h para cada aniversariante do dia.
            </div>
          </div>
        </div>

        {/* Toggle enxuto tipo shadcn ─────────────────────────── */}
        <button
          onClick={toggleEnabled}
          disabled={saving}
          data-testid="birthday-toggle-enabled"
          role="switch"
          aria-checked={enabled}
          className={`relative inline-flex w-10 h-6 items-center rounded-full transition
            ${enabled ? "bg-slate-900" : "bg-slate-200"}
            disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          <span
            className={`h-5 w-5 rounded-full bg-white shadow transform transition
              ${enabled ? "translate-x-4" : "translate-x-0.5"}`}
          />
        </button>
      </div>

      {/* Corpo ──────────────────────────────────────────────── */}
      <div className="px-5 py-4 grid md:grid-cols-2 gap-4">
        {/* Coluna esquerda: contador + ação ────────────────── */}
        <div className="space-y-3">
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 flex items-center justify-between">
            <div>
              <div className="text-[10.5px] uppercase tracking-wide text-slate-500 font-medium">
                Aniversariantes hoje
              </div>
              <div
                className="text-xl font-semibold text-slate-900 tabular-nums leading-tight"
                data-testid="birthday-today-count"
              >
                {todayCount ?? "—"}
              </div>
            </div>
            <button
              onClick={dispatchNow}
              disabled={dispatching || noBirthdaysToday}
              data-testid="birthday-dispatch-now"
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              <PaperPlaneTilt size={12} weight="bold" />
              {dispatching ? "Enviando…" : "Enviar agora"}
            </button>
          </div>

          {noBirthdaysToday && (
            <div className="text-[11.5px] text-slate-500 flex items-start gap-1.5 leading-snug">
              <Warning size={12} weight="fill" className="flex-shrink-0 mt-0.5 text-slate-400" />
              <span>
                Nenhum cliente faz aniversário hoje. A IA pergunta a data de nascimento
                automaticamente durante o atendimento.
              </span>
            </div>
          )}
        </div>

        {/* Coluna direita: preview + editar ───────────────── */}
        <div className="rounded-lg border border-slate-100 bg-slate-50/50 p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10.5px] uppercase tracking-wide text-slate-500 font-medium">
              Mensagem
            </div>
            <button
              onClick={() => { setEditing(!editing); setDraft(settings.message_template); }}
              data-testid="birthday-edit-template"
              className="text-[11px] text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 font-medium"
            >
              <PencilSimple size={11} />
              {editing ? "Cancelar" : "Editar"}
            </button>
          </div>

          {editing ? (
            <>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={4}
                maxLength={800}
                data-testid="birthday-template-input"
                className="w-full rounded-md px-2.5 py-2 text-[12.5px] bg-white text-slate-800 border border-slate-200 focus:outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-900/10 resize-none"
              />
              <div className="text-[10.5px] text-slate-500 mt-1.5 leading-snug">
                Variáveis disponíveis:{" "}
                {VARS_PT.map((v, i) => (
                  <span key={v.token}>
                    <code
                      className="bg-slate-200 text-slate-700 px-1 py-0.5 rounded text-[10px] font-mono"
                      title={v.desc}
                    >
                      {v.token}
                    </code>
                    {i < VARS_PT.length - 1 ? " " : ""}
                  </span>
                ))}
              </div>
              <button
                onClick={saveTemplate}
                disabled={saving || !draft.trim()}
                data-testid="birthday-template-save"
                className="mt-2 w-full text-[12px] font-medium bg-slate-900 text-white rounded-md py-1.5 hover:bg-slate-800 disabled:opacity-50 transition"
              >
                {saving ? "Salvando…" : "Salvar"}
              </button>
            </>
          ) : (
            <div
              className="text-[12.5px] text-slate-700 leading-relaxed"
              data-testid="birthday-preview-bubble"
            >
              {preview}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
